/**
 * components/layout/AppLayout.tsx
 *
 * Main authenticated layout wrapper with sidebar, breadcrumbs, and page header.
 * Updated: added optional titleClassName prop to allow per-page font weight overrides.
 * Updated Apr 2026: added noPadding prop — when true, breadcrumbs are rendered in
 *   their own padded strip and children fill the rest flush (used by ClientDetail).
 * Updated Apr 2026 — noPadding mode: mt-4 only applied when breadcrumbs are present;
 *   title/description/actions header rendered in both branches so noPadding pages can
 *   still use the page header.
 * Updated Jun 2026: renders ImpersonationBanner when admin is in impersonation mode;
 *   offsets content by currentBannerHeight (dynamic — responds to banner collapse).
 */

import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { ChevronRight } from 'lucide-react';
import ImpersonationBanner from '../admin/ImpersonationBanner';
import { useImpersonation } from '../../contexts/ImpersonationContext';

interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  titleClassName?: string;
  description?: string;
  breadcrumbs?: { label: string; path?: string }[];
  actions?: ReactNode;
  /** When true children receive no px/py padding; breadcrumbs still appear in their strip. */
  noPadding?: boolean;
}

function BreadcrumbNav({ breadcrumbs, className }: { breadcrumbs: { label: string; path?: string }[]; className?: string }) {
  return (
    <nav className={`flex items-center gap-2 text-sm ${className ?? ''}`}>
      {breadcrumbs.map((crumb, index) => (
        <div key={index} className="flex items-center gap-2">
          {index > 0 && <ChevronRight className="w-4 h-4 text-neutral-400" />}
          {crumb.path ? (
            <Link to={crumb.path} className="text-neutral-500 hover:text-neutral-700 transition-colors">
              {crumb.label}
            </Link>
          ) : (
            <span className="text-neutral-900 font-medium">{crumb.label}</span>
          )}
        </div>
      ))}
    </nav>
  );
}

export default function AppLayout({
  children,
  title,
  titleClassName,
  description,
  breadcrumbs,
  actions,
  noPadding = false,
}: AppLayoutProps) {
  const { currentBannerHeight } = useImpersonation();
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const topOffset = currentBannerHeight;

  return (
    <div className="flex bg-neutral-100" style={{ minHeight: '100vh', paddingTop: topOffset }}>
      <ImpersonationBanner />
      <Sidebar />

      <main className="flex-1 ml-[280px] overflow-auto flex flex-col" style={{ minHeight: `calc(100vh - ${topOffset}px)` }}>
        {noPadding ? (
          <>
            {hasBreadcrumbs && (
              <BreadcrumbNav breadcrumbs={breadcrumbs} className="px-6 pt-6 pb-0" />
            )}
            {(title || actions) && (
              <div className="flex items-start justify-between px-6 mt-4 mb-2">
                <div>
                  {title && (
                    <h1 className={`text-3xl text-neutral-900 mb-2 ${titleClassName ?? 'font-semibold'}`}>
                      {title}
                    </h1>
                  )}
                  {description && (
                    <p className="text-lg text-neutral-500">{description}</p>
                  )}
                </div>
                {actions && (
                  <div className="flex-1 flex items-center">
                    {actions}
                  </div>
                )}
              </div>
            )}
            <div className={`flex-1 flex flex-col ${hasBreadcrumbs ? 'mt-4' : ''}`}>
              {children}
            </div>
          </>
        ) : (
          <div className="max-w-7xl mx-auto px-6 py-8 w-full">
            {hasBreadcrumbs && (
              <BreadcrumbNav breadcrumbs={breadcrumbs} className="mb-4" />
            )}

            {(title || actions) && (
              <div className="flex items-start justify-between mb-8">
                <div>
                  {title && (
                    <h1 className={`text-3xl text-neutral-900 mb-2 ${titleClassName ?? 'font-semibold'}`}>
                      {title}
                    </h1>
                  )}
                  {description && (
                    <p className="text-lg text-neutral-500">{description}</p>
                  )}
                </div>
                {actions && (
                  <div className="flex-1 flex items-center">
                    {actions}
                  </div>
                )}
              </div>
            )}

            {children}
          </div>
        )}
      </main>
    </div>
  );
}
